import { css, useTheme } from '@emotion/react'
import { observer, inject } from 'mobx-react'
import _ from 'lodash'
import dayjs from 'dayjs'
import { SVGWrapper } from '../../../components'
import { CircleX, PiggyBank, Link } from '../../../assets/icons'
import { numberToDollars, withRouterProps } from '../../../utils'
import PlaidRefresh from '../investments/components/PlaidRefresh'
import {
  SectionBalance,
  DollarSign,
  SectionRow,
  SectionLabel,
  SectionSubLabel,
  NameField,
  BalanceField,
  EditButton,
  SVGContainer,
  SVGPlaceholder,
} from './DashboardStyle'
import DashboardDeleteAccount from './DashboardDeleteAccount'
import { useState } from 'react'
import { planTypeIdToAccountKey } from '../../../constants'

const typeToPathKey = {
  Annuity: 'annuity',
  Institutional: 'institutional',
  NonGc: 'non-gc',
  Pension: 'pension',
}

const DashboardAccount = props => {
  const {
    features,
    account: { managedAccount },
    findPlaidAccountById,
    accountInsight: { sortedContributionList },
  } = props.store
  const { name, balance, type, id, contributing, employerSponsoredAccount, plaidLinked } =
    props.account
  const { showLumpSumNotice } = props
  const theme = useTheme()
  const [state, setState] = useState({
    deleteStatus: 'done',
    deleteActive: false,
  })
  const plaidAccount = findPlaidAccountById(Number(id))
  const plaidAccountLastRefreshDate = _.get(plaidAccount, 'lastRefreshedOn', null)
  const acctContributions = sortedContributionList.find(item => item.accountId === id) ?? null
  const showPiggy = acctContributions !== null

  const toggleDeleteModal = () => {
    setState({
      deleteActive: !state.deleteActive,
      deleteStatus: 'done',
    })
  }

  const handleEdit = () => {
    const { navigate, account } = props
    const { type, id, planType } = account
    const accountType = planTypeIdToAccountKey[planType]
    navigate(`/accounts/${typeToPathKey[type]}/${accountType}/${id}`)
  }

  const findNamebyId = () => {
    const { store } = props
    const { participantId } = props.account
    const { displayName, id: personId } = store.person
    const { firstName, id: spouseId } = store.spouse

    if (participantId === personId) {
      return displayName
    } else if (participantId === spouseId) {
      return firstName
    } else {
      return ''
    }
  }

  const deleteAccount = async (type, id) => {
    const { deleteAccount, getAllPensions } = props.store
    setState({ deleteStatus: 'loading' })

    try {
      await deleteAccount(type, id)
      await getAllPensions()
    } catch (err) {
      console.error(err)
      setState({ deleteStatus: 'error' })
    }
  }

  const handlePlaidRefreshSuccess = () => {
    props.store.getExistingPlaidLinks()
    props.store.getAccounts()
  }

  return (
    <SectionRow id={props.id}>
      <NameField>
        {!employerSponsoredAccount ? (
          <SVGContainer>
            <SVGWrapper
              onClick={toggleDeleteModal}
              svg={CircleX}
              fill='#b6c0c4'
              hoverFill='#053240'
              activeFill='#0495bf'
              size='large'
            />
          </SVGContainer>
        ) : (
          <SVGContainer>
            <SVGPlaceholder />
          </SVGContainer>
        )}
        <div>
          <SectionLabel>
            {name}
            {type === 'Institutional' && features.managedAccountAvailable && managedAccount
              ? '*'
              : ''}
            {type === 'Pension' && showLumpSumNotice && '**'}
          </SectionLabel>
          <SectionSubLabel>{findNamebyId()}</SectionSubLabel>
        </div>
      </NameField>
      {plaidLinked && (
        <div
          css={css`
            font-size: 14px;
            color: #7a8e96;
            margin-top: 23px;
          `}>
          <Link
            css={css`
              margin: 0 6px;
            `}
          />
          Linked account last refreshed{' '}
          {plaidAccountLastRefreshDate
            ? `${dayjs(plaidAccountLastRefreshDate).format('MM/DD/YYYY')} (${dayjs(
                plaidAccountLastRefreshDate
              ).fromNow()})`
            : 'Never'}
        </div>
      )}
      <BalanceField>
        {contributing && showPiggy && (
          <SVGWrapper
            svg={PiggyBank}
            fill='#b6c0c4'
            hoverFill='#053240'
            size='medium'
            cursor='default'
          />
        )}
        <SectionBalance>
          <DollarSign>$</DollarSign>
          {numberToDollars(balance, true)}
        </SectionBalance>
        <EditButton onClick={handleEdit}>Edit</EditButton>
        {plaidLinked && (
          <PlaidRefresh
            css={css`
              position: absolute;
              bottom: 0;
              right: 0;
              font-size: 12px;
              color: ${theme.cardEditButtonColor};
              cursor: pointer;
              background-color: transparent;
              text-decoration: underline;
              border: none;
            `}
            accountId={id}
            onSuccess={handlePlaidRefreshSuccess}
          />
        )}
      </BalanceField>
      <DashboardDeleteAccount
        handleConfirmation={() => deleteAccount(type, id)}
        isModalOpen={state.deleteActive}
        toggleModal={toggleDeleteModal}
        loading={state.deleteStatus === 'loading'}
        error={state.deleteStatus === 'error'}
      />
    </SectionRow>
  )
}

export default withRouterProps(inject('store')(observer(DashboardAccount)))
